import { defineStore } from 'pinia';
import type {
  UserPermission,
  CourseInstanceDetails,
  UserBasic,
  Language,
  Degree,
  Program,
  Faculty,
  ProfileUser,
  Enrollment,
  UserProgram,
  UserStatus,
  UserTask,
} from 'src/types';
import type { User } from 'src/interfaces/user';
import { userManager } from 'src/boot/oidc';
import { useOidcStore } from 'src/stores/oidc';
import type { UserContact } from 'src/interfaces/usercontact';
import { UserContactType } from 'src/interfaces/usercontact';
import qs from 'qs';
import { date } from 'quasar';
import api from 'src/utils/api';

let fetchPromise: Promise<void> | null = null;

export const useProfileStore = defineStore('profile', {
  state: () => ({
    user: null as (User & ProfileUser & { '@id': string }) | null,
    loading: false,
    blocked: false,
    invoice: false,
    status: null as UserStatus | null,
    courses: [] as CourseInstanceDetails[],
    enrollments: [] as Enrollment[],
    teachCourses: [] as CourseInstanceDetails[],
    userPermissions: [] as UserPermission[],
    caslRules: [] as Array<{
      action: string | string[];
      subject: string;
      condition?: Record<string, unknown> | null;
    }>,
    coordinateLanguages: [] as Language[],
    degrees: [] as Degree[],
    programs: [] as Program[],
    faculties: [] as Faculty[],
    courseCategories: [] as { id: string; name: string }[],
    error: null as Error | null,
    userPrograms: [] as UserProgram[],
    contacts: [] as UserContact[],
    effectiveRoles: [] as string[],
  }),
  actions: {
    async fetch() {
      this.loading = true;
      try {
        const oidcStore = useOidcStore();
        const employeeNumber = oidcStore.employeeNumber;

        let profileData: any;
        let myAcadResponse: any;

        if (employeeNumber) {
          // Use combined endpoint — single request for profile + academic data
          const combined = await api(
            `/api/v2/users/${employeeNumber}/profile`,
            { params: { includeAcademic: true } },
          ).then((r) => r.json());

          profileData = combined.profile;
          myAcadResponse = combined.academic;
        } else {
          // Fallback: fetch profile and academic data in parallel
          [profileData, myAcadResponse] = await Promise.all([
            api('/api/v2/profile').then((r) => r.json()),
            api('/api/v2/my_academic').then((r) => r.json()),
          ]);
        }

        const {
          user,
          status,
          invoice,
          blocked,
          effectiveRoles,
          caslRules,
          userPermissions,
        } = profileData;

        this.user = user;
        this.status = status;
        this.invoice = invoice;
        this.blocked = blocked;
        this.effectiveRoles = effectiveRoles;
        this.caslRules = caslRules || [];
        this.userPermissions = userPermissions || [];

        if (
          myAcadResponse.code === 401 &&
          typeof myAcadResponse.message === 'string' &&
          myAcadResponse.message.includes('Expired')
        ) {
          await userManager.signinRedirect();
          return;
        }

        // Fetch user programs (depends on user @id from profile)
        const userProgramsData = await api(
          user['@id'] + '/user_programs',
        ).then((r) => r.json());

        this.userPrograms = userProgramsData['hydra:member'];

        const {
          courses,
          teachCourses,
          programs,
          degrees,
          faculties,
          coordinateLanguages,
          enrollments,
          courseCategories,
        } = myAcadResponse;

        this.courses = courses;
        this.enrollments = enrollments;
        this.teachCourses = teachCourses;
        this.programs = programs;
        this.degrees = degrees;
        this.faculties = faculties;
        this.coordinateLanguages = coordinateLanguages;
        this.courseCategories = courseCategories;

        this.loading = false;
      } catch (e) {
        this.error = e as Error;
        console.log(e);
        if (e?.response) {
          console.log(e.response);
        }
        this.loading = false;
      }
    },
    async resolve() {
      if (this.user) return;
      // Deduplicate: reuse in-flight fetch if one is already running
      if (!fetchPromise) {
        fetchPromise = this.fetch().finally(() => {
          fetchPromise = null;
        });
      }
      await fetchPromise;
    },
    async updateUser(id: number, data: Partial<User>) {
      await apiClient.put(`/api/v2/users/${id}`, data).then((r) => r.data);
      await this.fetch();
    },
    async updateUserBasic(data: Partial<UserBasic>) {
      const response = await api(this.user?.basic?.['@id'], {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      const result = await response.json();

      if (this.user) this.user.basic = result;
    },
    async fetchContacts(params: { category?: string | string[] } = {}) {
      const response = await api(`${this.user?.['@id']}/contacts`, {
        params: {
          ...params,
          itemsPerPage: 999,
        },
      });
      const result = await response.json();
      // merge with existing contacts, with @id as key
      this.contacts = Object.values({
        ...Object.fromEntries(this.contacts.map((c) => [c['@id'], c])),
        ...Object.fromEntries(result['hydra:member'].map((c) => [c['@id'], c])),
      });
    },
    async updateEmergencyContact(data: {
      mobile: {
        dialCode: string;
        number: string;
      };
      relationship: string;
      personName: string;
    }) {
      const contacts = this.contacts.filter((c) => c.category === 'Emergency');
      // fetch
      const existingMobile = [
        contacts.find((c) => c.type === 4),
        data.mobile,
        4,
      ];
      const existingRelation = [
        contacts.find((c) => c.type === 7),
        data.relationship,
        7,
      ];
      const existingPersonName = [
        contacts.find((c) => c.type === 6),
        data.personName,
        6,
      ];

      await Promise.all(
        [existingMobile, existingRelation, existingPersonName].map(
          ([existing, value, type]) => {
            if (existing) {
              return api(existing['@id'], {
                method: 'PUT',
                body: JSON.stringify({ combined: value }),
              });
            } else {
              return api(`user_contacts`, {
                method: 'POST',
                body: JSON.stringify({
                  combined: value,
                  category: 'Emergency',
                  user: this.user['@id'],
                  type,
                }),
              });
            }
          },
        ),
      );
    },
    async updatePersonalContact(data: {
      mobile: {
        dialCode: string;
        number: string;
      };
      address: Record<string, any>;
    }) {
      const contacts = this.contacts.filter((c) => c.category === 'Personal');
      // fetch
      const existingMobile = [
        contacts.find((c) => c.type === UserContactType.MOBILE),
        data.mobile,
        UserContactType.MOBILE,
      ];
      const existingAddress = [
        contacts.find((c) => c.type === UserContactType.ADDRESS),
        data.address,
        UserContactType.ADDRESS,
      ];

      await Promise.all(
        [existingMobile, existingAddress].map(([existing, value, type]) => {
          if (existing) {
            return api(existing['@id'], {
              method: 'PUT',
              body: JSON.stringify({ combined: value }),
            });
          } else {
            return api(`user_contacts`, {
              method: 'POST',
              body: JSON.stringify({
                combined: value,
                category: 'Personal',
                user: this.user['@id'],
                type,
              }),
            });
          }
        }),
      );
    },
    async updateTeachingPracticeSupervisorContact(data: {
      chineseName: string;
      englishName: string;
      email: string;
      phone: {
        dialCode: string;
        number: string;
      };
    }) {
      const contacts = this.contacts.filter(
        (c) => c.category === 'Teaching Practice Supervisor',
      );

      // Create contact entries for each field
      const contactEntries = [
        {
          existing: contacts.find(
            (c) =>
              c.type === UserContactType.CUSTOM &&
              c.customType === 'chineseName',
          ),
          value: data.chineseName,
          type: UserContactType.CUSTOM,
          customType: 'chineseName',
        },
        {
          existing: contacts.find(
            (c) =>
              c.type === UserContactType.CUSTOM &&
              c.customType === 'englishName',
          ),
          value: data.englishName,
          type: UserContactType.CUSTOM,
          customType: 'englishName',
        },
        {
          existing: contacts.find((c) => c.type === UserContactType.EMAIL),
          value: data.email,
          type: UserContactType.EMAIL,
        },
        {
          existing: contacts.find((c) => c.type === UserContactType.MOBILE),
          value: data.phone,
          type: UserContactType.MOBILE,
        },
      ];

      await Promise.all(
        contactEntries.map(({ existing, value, type, customType }) => {
          const body: any = {
            combined: value,
            category: 'Teaching Practice Supervisor',
            user: this.user['@id'],
            type,
          };

          if (customType) {
            body.customType = customType;
          }

          if (existing) {
            return api(existing['@id'], {
              method: 'PUT',
              body: JSON.stringify({ combined: value }),
            });
          } else {
            return api(`user_contacts`, {
              method: 'POST',
              body: JSON.stringify(body),
            });
          }
        }),
      );
    },
  },
  getters: {
    userLoaded(state) {
      return (
        !state.loading && (state.user !== null || state.user !== undefined)
      );
    },
    roles(state) {
      return Object.fromEntries(
        (state.user?.roles || []).map((r) => [r, true]),
      );
    },
    effectiveRolesMap(state) {
      return Object.fromEntries(state.effectiveRoles.map((r) => [r, true]));
    },
    isSuperAdmin(state) {
      return state.user?.roles.includes('ROLE_SUPER_ADMIN');
    },
    isStudent(state) {
      return state.user?.roles.includes('ROLE_STUDENT');
    },
    surveyTasks(state) {
      return (
        state.status?.tasks?.filter(
          (t): t is UserTask => t.type === 'COMPLETE_SURVEY',
        ) || []
      );
    },
    emergencyContact(state) {
      if (
        state.contacts.filter((c) => c.category === 'Emergency').length === 0
      ) {
        return null;
      }

      const emergencyContact = state.contacts.filter(
        (c) => c.category === 'Emergency',
      );

      return {
        mobile:
          emergencyContact?.find((c) => c.type === UserContactType.MOBILE)
            ?.combined || '',
        relationship:
          emergencyContact?.find(
            (c) => c.type === UserContactType.EMERGENCY_RELATIONSHIP,
          )?.combined || '',
        personName:
          emergencyContact?.find(
            (c) => c.type === UserContactType.EMERGENCY_PERSON,
          )?.combined || '',
      };
    },
    personalContact(state) {
      if (
        state.contacts.filter((c) => c.category === 'Personal').length === 0
      ) {
        return null;
      }

      const personalContacts = state.contacts.filter(
        (c) => c.category === 'Personal',
      );

      return {
        mobile:
          personalContacts.find((c) => c.type === UserContactType.MOBILE)
            ?.combined || '',
        address:
          personalContacts.find((c) => c.type === UserContactType.ADDRESS)
            ?.combined || '',
      };
    },
    teachingPracticeSupervisorContact(state) {
      if (
        state.contacts.filter(
          (c) => c.category === 'Teaching Practice Supervisor',
        ).length === 0
      ) {
        return null;
      }
      const teachingPracticeSupervisorContact = state.contacts.filter(
        (c) => c.category === 'Teaching Practice Supervisor',
      );

      return {
        chineseName:
          teachingPracticeSupervisorContact.find(
            (c) =>
              c.type === UserContactType.CUSTOM &&
              c.customType === 'chineseName',
          )?.combined || '',
        englishName:
          teachingPracticeSupervisorContact.find(
            (c) =>
              c.type === UserContactType.CUSTOM &&
              c.customType === 'englishName',
          )?.combined || '',
        email:
          teachingPracticeSupervisorContact.find(
            (c) => c.type === UserContactType.EMAIL,
          )?.combined || '',
        phone:
          teachingPracticeSupervisorContact.find(
            (c) => c.type === UserContactType.MOBILE,
          )?.combined || '',
      };
    },
    personalContactLastConfirmedAt(state) {
      const dates: Date[] = state.contacts
        .filter((c) => c.category === 'Personal' && c.confirmedAt)
        .map((c) => new Date(c.confirmedAt));
      return new Date(date.getMaxDate(dates[0], ...dates));
    },
  },
});
